import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, Trash2, Home, Car, Target } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { VerifiedUnavailable } from "@/components/VerifiedUnavailable";
import DealOfTheDay from "@/components/DealOfTheDay";
import AlertPreferences from "@/components/AlertPreferences";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const fmt = (n) => (n || n === 0 ? `$${Number(n).toLocaleString()}` : "—");
const linkFor = (it) => (it.item_type === "vehicle" ? `/vehicles/${encodeURIComponent(it.item_id)}` : `/homes/${encodeURIComponent(it.item_id)}`);

function TargetPrice({ it, onSaved }) {
  const [val, setVal] = useState(it.target_price ? String(it.target_price) : "");
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  const save = async (clear) => {
    setBusy(true);
    const target = clear ? null : (val ? Number(val.replace(/[^0-9.]/g, "")) : null);
    try {
      await api.put(`/watchlist/${it.item_type}/${encodeURIComponent(it.item_id)}/target`, { target_price: target });
      toast.success(clear || target === null ? "Target cleared" : `Target set to ${fmt(target)}`);
      setEditing(false); if (clear) setVal(""); onSaved?.(it.item_id, target);
    } catch { toast.error("Could not save target"); }
    finally { setBusy(false); }
  };

  if (!editing && it.target_price) {
    return (
      <div className="mt-3 flex items-center justify-between gap-2 border-t border-border pt-3" data-testid="watchlist-target-set">
        <span className="font-mono-data text-[10px] uppercase tracking-wider text-accent">🎯 Target {fmt(it.target_price)}</span>
        <div className="flex gap-1">
          <button onClick={() => setEditing(true)} data-testid="watchlist-target-edit" className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground">Edit</button>
          <button onClick={() => save(true)} disabled={busy} data-testid="watchlist-target-clear" className="font-mono-data text-[10px] uppercase tracking-wider text-rose-500 hover:text-rose-400">Clear</button>
        </div>
      </div>
    );
  }
  if (!editing) {
    return (
      <button onClick={() => setEditing(true)} data-testid="watchlist-target-add" className="mt-3 w-full border-t border-border pt-3 font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground hover:text-accent text-left">+ Set target price alert</button>
    );
  }
  return (
    <div className="mt-3 border-t border-border pt-3 flex items-center gap-2" data-testid="watchlist-target-editor">
      <div className="relative flex-1">
        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
        <Input value={val} onChange={(e) => setVal(e.target.value)} inputMode="numeric" placeholder="Target price" data-testid="watchlist-target-input" className="h-8 pl-6 text-sm" />
      </div>
      <Button size="sm" className="rounded-full h-8" disabled={busy} data-testid="watchlist-target-save" onClick={() => save(false)}>Save</Button>
    </div>
  );
}

export default function Portfolio({ embedded = false }) {
  const { user, loading: authLoading } = useAuth();
  const [watchlist, setWatchlist] = useState([]);

  useEffect(() => {
    if (user) api.get("/watchlist").then((r) => setWatchlist(r.data)).catch(() => {});
  }, [user]);

  if (authLoading) return <div className="min-h-[60vh] flex items-center justify-center font-mono-data text-sm text-muted-foreground animate-pulse">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  const remove = async (it) => {
    try {
      await api.delete(`/watchlist/${it.item_type}/${encodeURIComponent(it.item_id)}`);
      setWatchlist((x) => x.filter((y) => y.id !== it.id));
      toast.success("Removed");
    } catch { toast.error("Could not remove"); }
  };

  return (
    <div className={`max-w-[1600px] mx-auto px-5 sm:px-8 ${embedded ? "pt-2 pb-12" : "py-12"}`}>
      {!embedded && (
        <>
          <div className="flex items-center gap-3 mb-2">
            <Eye className="w-6 h-6" strokeWidth={1.5} />
            <h1 className="font-display text-4xl tracking-tighter font-medium">Deals / Watchlist</h1>
          </div>
          <p className="text-muted-foreground mb-8">Today's top verified deals, plus the homes and vehicles you're keeping an eye on.</p>
        </>
      )}

      <DealOfTheDay />

      <AlertPreferences />

      <h2 className="font-display text-2xl font-medium mb-5">My Watchlist</h2>
      <div data-testid="watchlist-panel">
        {!watchlist.length ? (
          <VerifiedUnavailable testId="watchlist-empty" note="Tap “Add to Watchlist” on any home or vehicle to monitor it here." />
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {watchlist.map((it) => (
              <motion.div key={it.id} whileHover={{ y: -4 }} className="relative bg-surface border border-border rounded-xl p-5 overflow-hidden" data-testid="watchlist-item">
                {it.target_hit && (
                  <div className="absolute top-0 right-0 bg-emerald-500 text-white font-mono-data text-[9px] uppercase tracking-wider px-3 py-1 rounded-bl-lg flex items-center gap-1" data-testid="watchlist-target-hit-ribbon">
                    <Target className="w-3 h-3" />Target hit
                  </div>
                )}
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    {it.item_type === "vehicle" ? <Car className="w-4 h-4 text-muted-foreground" /> : <Home className="w-4 h-4 text-muted-foreground" />}
                    <span className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">{it.item_type === "vehicle" ? "vehicle" : "home"}</span>
                  </div>
                  <button onClick={() => remove(it)} data-testid="watchlist-remove" className="text-muted-foreground hover:text-foreground"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
                <Link to={linkFor(it)} className="block group">
                  <div className="font-editorial text-lg italic mt-2 line-clamp-1 group-hover:text-accent transition-colors">{it.data?.title || it.item_id}</div>
                  <div className="font-mono-data text-sm mt-1 flex items-center gap-2">
                    <span className={it.target_hit ? "text-emerald-500" : ""}>{fmt(it.current_price ?? it.data?.price)}</span>
                    {it.off_market && <span className="text-[10px] uppercase tracking-wider text-muted-foreground">off-market</span>}
                  </div>
                </Link>
                <TargetPrice it={it} onSaved={(id, target) => setWatchlist((x) => x.map((y) => y.item_id === id ? { ...y, target_price: target, target_hit: !!(target && (y.current_price ?? y.data?.price) && !y.off_market && (y.current_price ?? y.data?.price) <= target) } : y))} />
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
